import { object, array } from 'prop-types';
import React from 'react';
import styled, { css, withTheme } from 'styled-components';

import TableBody from 'ravenjs/lib/TableBody';
import TableCell from 'ravenjs/lib/TableCell';
import TableRow from 'ravenjs/lib/TableRow';
import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { getZebraStyle } from 'ravenjs/utils/table';
import { STICKY_COLUMN_POSITION } from 'ravenjs/constants/list';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import { themeGet } from 'ravenjs/utils/theme';
import Ellipsis from 'ravenjs/components/Ellipsis';
import Typography from 'ravenjs/lib/Typography';

import { DATA_LIST_VIEW_SCHEMA } from 'constants/proptypes';
import { addStickyOffsetInColumns, getSelectStickyProps } from 'utils/dataList';
import { DEFAULT_COLORS } from 'constants/colors';

export const TypographyStyled = styled(Typography)`
    ${props => {
        const styles = get(props, 'theme.DataView.primaryText.styles', {});
        const fontFamily = styles.fontFamily ? `${styles.fontFamily} !important` : '';
        return {
            ...styles,
            fontFamily,
        };
    }}
    ${({ disabledColor: disabledColorProps, isDisabled, theme }) => {
        const disabledColor = themeGet(
            `palette.${disabledColorProps}.color`,
            DEFAULT_COLORS.BLACK
        )({ theme });
        let disabledCSS = '';
        if (isDisabled) {
            disabledCSS = css`
                color: ${disabledColor};
            `;
        }
        return css`
            ${disabledCSS};
        `;
    }}
    display: block;
`;

const NoDataStyled = styled(Typography)`
    ${({ theme }) => theme.media.down('md')`
        text-align: left;
    `};
    ${({ theme }) => theme.media.up('md')`
        text-align: center;
    `};
`;

function DataListBody(props) {
    const { actions, data, schema, theme } = props;
    const cellTypes = get(schema, 'cellTypes', {});
    let columns = get(schema, 'columns', []);
    const noData = get(schema, 'noData', 'No Data Available');
    const rowProps = get(schema, 'rows', {});
    const rowClickHandler = getHandler(actions, get(rowProps, 'onClickRow'));
    const selectedProps = get(schema, 'selected', {});
    const tooltipTypes = get(schema, 'tooltipTypes', {});
    const { renderSelectableRef = '', width: selectBoxWidth } = selectedProps;
    const isRowDisabled = getHandler(actions, 'isRowDisabled');
    const renderSelectable = getHandler(actions, renderSelectableRef);
    const hasSelectColumn = typeof renderSelectable === 'function';
    const selectColumnWidth = selectBoxWidth || '50px';
    const hasStickyColumnsAtLeft = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.LEFT)
    );
    const hasStickyColumnsAtRight = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.RIGHT)
    );

    if (hasStickyColumnsAtLeft) {
        columns = addStickyOffsetInColumns(
            columns,
            STICKY_COLUMN_POSITION.LEFT,
            hasSelectColumn ? selectColumnWidth : '0px',
            theme
        );
    }

    if (hasStickyColumnsAtRight) {
        columns = addStickyOffsetInColumns(columns, STICKY_COLUMN_POSITION.RIGHT, '0px', theme);
    }

    const selectCellStickyProps = getSelectStickyProps(columns);

    if (isEmpty(data)) {
        const colSpan =
            typeof renderSelectable === 'function' ? columns.length + 1 : columns.length;
        return (
            <TableBody>
                <TableRow>
                    <TableCell colSpan={colSpan} noDataAvailable>
                        <NoDataStyled gutterBottom="0" gutterTop="0" type="subheading">
                            {noData}
                        </NoDataStyled>
                    </TableCell>
                </TableRow>
            </TableBody>
        );
    }

    const onClickRow = row => {
        return () => {
            callFunc(rowClickHandler, row);
        };
    };

    return (
        <TableBody>
            {data.map((row, rowIdx) => {
                const { keyProp = 'id', ...restRowProps } = rowProps;
                const key = get(row, keyProp, `${keyProp}_${rowIdx}`);
                const zebra = getZebraStyle(rowIdx, rowProps.zebra);
                const { zebraColor } = rowProps;
                const isDisabled = callFunc(isRowDisabled, { row });
                return (
                    <TableRow
                        {...rowProps}
                        {...restRowProps}
                        isDisabled={isDisabled}
                        handlers={actions}
                        key={key}
                        keyProp={keyProp}
                        row={row}
                        schema={schema}
                        zebraColor={zebraColor}
                        zebra={zebra}
                        onClick={onClickRow(row)}
                    >
                        {hasSelectColumn && (
                            <TableCell
                                key={`${key}__checkbox`}
                                width={selectColumnWidth}
                                align="center"
                                cellProps={{
                                    noPadding: true,
                                }}
                                col={selectCellStickyProps}
                                zebra={zebra}
                                zebraColor={zebraColor}
                            >
                                {callFunc(renderSelectable, row)}
                            </TableCell>
                        )}
                        {columns.map((bodyCol, bodyColIdx) => {
                            const {
                                actions: bodyColActions = [],
                                cellProps = {},
                                ...restBodyCol
                            } = bodyCol;
                            const bodyColFallback =
                                'defaultValue' in cellProps ? cellProps.defaultValue : '-';
                            let bodyColValue = get(row, bodyCol.id, bodyColFallback);

                            if (typeof bodyColValue === 'number') {
                                bodyColValue = bodyColValue.toString();
                            }
                            const maxLine = get(cellProps, 'maxLine', 1);
                            const color = get(cellProps, 'color', DEFAULT_COLORS.BLACK);
                            const hideBrowserTooltip =
                                get(cellProps, 'hideBrowserTooltip', false) === true;
                            const align = get(bodyCol, 'align', '');
                            const title = get(bodyCol, 'title', '');
                            let key =
                                bodyColValue !== '-'
                                    ? bodyColValue
                                    : get(row, bodyCol.keyProp, `${bodyCol.keyProp || bodyCol.id}`);
                            key = `${key}_${bodyColIdx}`;

                            return (
                                <TableCell
                                    actions={bodyColActions}
                                    align={align}
                                    cellProps={cellProps}
                                    cellTypes={cellTypes}
                                    col={restBodyCol}
                                    data-title={title}
                                    handlers={actions}
                                    key={key}
                                    row={row}
                                    schema={schema}
                                    tooltipTypes={tooltipTypes}
                                    zebra={zebra}
                                    zebraColor={zebraColor}
                                >
                                    <TypographyStyled
                                        disabledColor={rowProps.disabledColor}
                                        isDisabled={isDisabled}
                                        gutterBottom="0"
                                        gutterTop="0"
                                        type="span"
                                        title={hideBrowserTooltip ? '' : bodyColValue}
                                    >
                                        {maxLine !== 1 ? (
                                            <Ellipsis
                                                content={bodyColValue}
                                                maxLine={maxLine}
                                                color={color}
                                            />
                                        ) : (
                                            bodyColValue
                                        )}
                                    </TypographyStyled>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                );
            })}
        </TableBody>
    );
}

DataListBody.propTypes = {
    actions: object,
    data: array,
    schema: DATA_LIST_VIEW_SCHEMA,
    theme: object.isRequired,
};

DataListBody.defaultProps = {
    actions: null,
    data: null,
    schema: null,
};

export { DataListBody as DataListBodyUnwrapped };
export default withTheme(DataListBody);
